export * from './qraft/index';

import { requestFn } from '@openapi-qraft/react';
import type {
  OperationSchema,
  RequestFnInfo,
  RequestFnOptions,
  RequestFnResponse,
} from '@openapi-qraft/tanstack-query-react-types';
import {
  type Mutation,
  MutationCache,
  QueryClient,
} from '@tanstack/react-query';
import { isAdminPath } from '@/admin/paths';
import { apiLanguage } from '~/i18n';
import { createAPIClient } from './qraft/index';

// HTTP status of each failed request's error body; the body carries none itself.
const errorStatus = new WeakMap<object, number>();

/**
 * Exported because the language switcher has to invalidate it: the language
 * rides in a header, not the query key, so nothing makes a cached response look
 * stale after a switch. See `useInterfaceLanguage`.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // The console refetches on tab focus and reconnect, so a second tab shows
      // another's edits. The rest of the app doesn't yet: a separate decision.
      refetchOnWindowFocus: () => isAdminPath(),
      refetchOnReconnect: () => isAdminPath(),
      // In the console a 4xx won't change on retry, so show it now instead of
      // after ~7s of backoff. The rest of the app keeps the default three tries.
      retry: (failureCount, error) => {
        const status =
          typeof error === 'object' && error !== null
            ? errorStatus.get(error)
            : undefined;
        if (
          isAdminPath() &&
          status !== undefined &&
          status >= 400 &&
          status < 500
        )
          return false;
        return failureCount < 3;
      },
    },
  },
  mutationCache: new MutationCache({
    // Runs before a mutation's own `onSuccess`, so a page's specific message
    // still overwrites a generic one.
    onSuccess: (_data, _variables, _context, mutation) => {
      for (const listener of mutationSuccessListeners) listener(mutation);
    },
  }),
});

type MutationSuccessListener = (
  mutation: Mutation<unknown, unknown, unknown, unknown>,
) => void;

const mutationSuccessListeners = new Set<MutationSuccessListener>();

/**
 * Subscribes to every successful mutation and returns the unsubscribe. The
 * admin console uses it for its save banner, so member pages never announce.
 */
export function onMutationSuccess(
  listener: MutationSuccessListener,
): () => void {
  mutationSuccessListeners.add(listener);
  return () => {
    mutationSuccessListeners.delete(listener);
  };
}

export const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || '/api';

/**
 * Sets the language header on every v3 request. The server answers labels and
 * option lists in the request's language, and without the header falls back to
 * the account's stored preference rather than the switcher's current one.
 *
 * Read per request, since `i18n.changeLanguage` can land between any two calls.
 */
async function customRequestFn<TData, TError>(
  schema: OperationSchema,
  requestInfo: RequestFnInfo,
  options?: RequestFnOptions,
): Promise<RequestFnResponse<TData, TError>> {
  const newRequestInfo = {
    ...requestInfo,
    credentials: 'include',
    // The console never sent it, and the server falls back to the account's
    // stored preference without it. Revisit when the console gets a language
    // selector.
    headers: isAdminPath()
      ? requestInfo.headers
      : { ...requestInfo.headers, 'x-wbs-language': apiLanguage() },
  };
  const result = await requestFn<TData, TError>(
    schema,
    newRequestInfo as RequestFnInfo,
    options,
  );
  if (
    result.error &&
    typeof result.error === 'object' &&
    result.response !== undefined
  ) {
    errorStatus.set(result.error, result.response.status);
  }
  return result;
}

const api_client = createAPIClient({
  baseUrl: API_ENDPOINT,
  requestFn: customRequestFn,
  queryClient,
});

/**
 * The viewer and the viewed group are read by every shell component, and those
 * mount in gated steps, so with no stale time each later mount refetches a
 * response that just landed. Every write that changes either one invalidates
 * it. A session-expiry bounce, which keys off a `getCurrentUser` 401, can lag
 * by up to this long.
 */
const APP_CONTEXT_STALE_TIME = 60_000;

queryClient.setQueryDefaults(api_client.v3.getCurrentUser.getQueryKey(), {
  staleTime: APP_CONTEXT_STALE_TIME,
});
// The key prefix with no parameters, so it matches every slug.
queryClient.setQueryDefaults(
  [{ ...api_client.v3.getGroupBySlug.schema, infinite: false }],
  { staleTime: APP_CONTEXT_STALE_TIME },
);

export default api_client.v3;
